// src/components/Dashboard.jsx
// Operational analytics — real Recharts wired to live Firestore data.
// Charts are expandable on mobile via a full-screen modal.

import React, { useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  ResponsiveContainer,
  BarChart, Bar,
  PieChart, Pie, Cell,
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import * as PPIcons from "./_icons";
import { Button, SectionHead, Segmented } from "./_primitives";

/* ── helpers ────────────────────────────────────────────────── */
function toDate(ts) {
  if (!ts) return null;
  if (typeof ts.toDate === "function") return ts.toDate();
  if (ts instanceof Date) return ts;
  if (typeof ts === "number") return new Date(ts);
  return null;
}

function getRangeStart(range) {
  if (range === "all") return null;
  const days = { "1d": 1, "7d": 7, "30d": 30, "90d": 90, "1y": 365 }[range] || 30;
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return d;
}

function applyRange(posts, rangeStart) {
  if (!rangeStart) return posts;
  return posts.filter(p => { const d = toDate(p.createdAt); return d && d >= rangeStart; });
}

function fmtDay(date) {
  return date.toLocaleDateString("en-US", { month: "numeric", day: "numeric" });
}

function deltaBadge(cur, prev) {
  if (!prev) return { label: "—", tone: "flat" };
  const diff = cur - prev;
  if (diff === 0) return { label: "=", tone: "flat" };
  const pct = Math.round(Math.abs(diff / (prev || 1)) * 100);
  return {
    label: `${diff > 0 ? "+" : "−"}${pct}%`,
    tone: diff > 0 ? "up" : "down",
  };
}

/* ── tooltip ─────────────────────────────────────────────────── */
function PPTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "var(--bg-elevated)", border: "1px solid var(--border)",
      borderRadius: "var(--radius-sm)", padding: "8px 12px",
      boxShadow: "var(--shadow)", fontSize: 12,
    }}>
      {label && <div style={{ fontWeight: 700, marginBottom: 4, color: "var(--text)" }}>{label}</div>}
      {payload.map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-dim)" }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: entry.color, display: "inline-block" }} />
          {entry.name}: <span style={{ fontWeight: 600, color: "var(--text)", marginLeft: 2 }}>{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

/* ── chart modal ─────────────────────────────────────────────── */
function ChartModal({ title, sub, onClose, children }) {
  return createPortal(
    <div
      style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.55)", display: "flex", flexDirection: "column", padding: 12 }}
      onClick={onClose}
    >
      <div
        style={{ background: "var(--bg)", borderRadius: "var(--radius-lg)", flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", maxHeight: "92dvh" }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid var(--border)" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text)" }}>{title}</div>
            {sub && <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 2 }}>{sub}</div>}
          </div>
          <button onClick={onClose} style={{ border: 0, background: "transparent", cursor: "pointer", color: "var(--text-dim)", fontSize: 22, lineHeight: 1, padding: 4 }}>×</button>
        </div>
        <div style={{ flex: 1, padding: "12px 8px 20px", minHeight: 0 }}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ── chart card ──────────────────────────────────────────────── */
function ChartCard({ title, sub, height = 220, chart }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="pp-surface" style={{ cursor: "pointer" }} onClick={() => setOpen(true)}>
        <SectionHead
          title={title}
          sub={sub}
          right={
            <button
              onClick={e => { e.stopPropagation(); setOpen(true); }}
              style={{ border: 0, background: "transparent", cursor: "pointer", color: "var(--text-faint)", padding: 4, display: "flex" }}
              aria-label="Expand chart"
            >
              <PPIcons.IconExternal style={{ width: 13, height: 13 }} />
            </button>
          }
        />
        <div style={{ padding: "4px 10px 16px", height }}>
          {chart(height)}
        </div>
      </div>
      {open && (
        <ChartModal title={title} sub={sub} onClose={() => setOpen(false)}>
          {chart("100%")}
        </ChartModal>
      )}
    </>
  );
}

/* ── stat tile ───────────────────────────────────────────────── */
function StatTile({ label, value, delta, deltaTone = "flat", tone }) {
  const toneColor = tone === "crit" ? "var(--pp-crit)"
    : tone === "mod" ? "var(--pp-mod)"
    : tone === "low" ? "var(--pp-low)"
    : "var(--text-strong)";
  return (
    <div className="pp-stat">
      <div className="pp-stat-label">{label}</div>
      <div className="pp-stat-value" style={{ color: toneColor }}>{value}</div>
      <div className={`pp-stat-delta pp-stat-delta--${deltaTone}`}>
        {deltaTone === "up"   && <PPIcons.IconTrending style={{ width: 12, height: 12 }} />}
        {deltaTone === "down" && <PPIcons.IconTrending style={{ width: 12, height: 12, transform: "scaleY(-1)" }} />}
        {delta} <span style={{ color: "var(--text-faint)", fontSize: 11 }}>vs prev</span>
      </div>
    </div>
  );
}

/* ── category bar (top friction) ─────────────────────────────── */
function CategoryBar({ name, cur, prev, max }) {
  const curPct = Math.min(100, (cur / max) * 100);
  const prevPct = Math.min(100, (prev / max) * 100);
  const diff = cur - prev;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ width: 130, fontSize: 13, fontWeight: 600, color: "var(--text)", flexShrink: 0 }}>{name}</div>
      <div style={{ flex: 1, position: "relative", height: 18, borderRadius: 4, background: "var(--surface-2)" }}>
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${prevPct}%`, background: "var(--border-strong)", borderRadius: 4, opacity: 0.55 }} />
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${curPct}%`, background: "var(--accent)", borderRadius: 4 }} />
      </div>
      <div style={{ width: 90, textAlign: "right", fontSize: 12, fontFamily: "var(--font-mono)", flexShrink: 0 }}>
        <span style={{ fontWeight: 700, color: "var(--text-strong)" }}>{cur}</span>
        <span style={{ color: "var(--text-faint)" }}> · {prev}</span>
        {diff !== 0 && (
          <span style={{ marginLeft: 4, color: diff > 0 ? "var(--pp-crit)" : "var(--pp-low)", fontSize: 11 }}>
            {diff > 0 ? `+${diff}` : diff}
          </span>
        )}
      </div>
    </div>
  );
}

/* ── heatmap (SVG, real data) ────────────────────────────────── */
function Heatmap({ data }) {
  const days  = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const hours = [0, 3, 6, 9, 12, 15, 18, 21];
  const max   = Math.max(1, ...data.flat());
  const CW = 26, CH = 22, LW = 36, LH = 18;
  const W  = LW + 24 * CW, H = LH + 7 * CH + 20;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
      {hours.map(h => (
        <text key={h} x={LW + h * CW + CW / 2} y={12} textAnchor="middle"
          fontSize="9" fill="var(--text-faint)" fontFamily="var(--font-mono)">{h}h</text>
      ))}
      {days.map((d, r) => (
        <React.Fragment key={d}>
          <text x={LW - 4} y={LH + r * CH + CH * 0.65} textAnchor="end"
            fontSize="9" fill="var(--text-faint)" fontFamily="var(--font-mono)">{d}</text>
          {Array.from({ length: 24 }, (_, c) => {
            const v = data[r]?.[c] || 0;
            const opacity = v === 0 ? 0.06 : 0.1 + (v / max) * 0.85;
            return (
              <rect key={c} x={LW + c * CW + 1} y={LH + r * CH + 1}
                width={CW - 2} height={CH - 2} rx="3"
                fill="var(--accent)" opacity={opacity}>
                <title>{d} {c}:00 — {v} report{v !== 1 ? "s" : ""}</title>
              </rect>
            );
          })}
        </React.Fragment>
      ))}
    </svg>
  );
}

/* ── main component ──────────────────────────────────────────── */
const SEVE_COLORS = {
  critical: "var(--pp-crit)",
  moderate: "var(--pp-mod)",
  low:      "var(--pp-low)",
};

function Dashboard({ posts = [], resolvedPosts = [], tasks = [], categories = [] }) {
  const [range, setRange]             = useState("30d");
  const [filterCategory, setFilterCat] = useState("");
  const [filterSeverity, setFilterSev] = useState("");

  const all = useMemo(() => [...posts, ...resolvedPosts], [posts, resolvedPosts]);

  /* date windows */
  const rangeStart = useMemo(() => getRangeStart(range), [range]);
  const prevStart  = useMemo(() => {
    if (!rangeStart) return null;
    const ms   = Date.now() - rangeStart.getTime();
    const prev = new Date(rangeStart.getTime() - ms);
    return prev;
  }, [rangeStart]);

  const current = useMemo(() => applyRange(all, rangeStart), [all, rangeStart]);
  const previous = useMemo(() => {
    if (!prevStart || !rangeStart) return [];
    return all.filter(p => { const d = toDate(p.createdAt); return d && d >= prevStart && d < rangeStart; });
  }, [all, prevStart, rangeStart]);

  /* apply user filters to chart data */
  const chartPosts = useMemo(() => current.filter(p =>
    (!filterCategory || p.category === filterCategory) &&
    (!filterSeverity || p.severity === filterSeverity)
  ), [current, filterCategory, filterSeverity]);

  /* stat tile counts */
  const counts = useMemo(() => ({
    total:     current.length,
    critical:  current.filter(p => p.severity === "critical" && !p.resolved).length,
    moderate:  current.filter(p => p.severity === "moderate" && !p.resolved).length,
    low:       current.filter(p => p.severity === "low"      && !p.resolved).length,
    resolved:  current.filter(p =>  p.resolved).length,
    openTasks: tasks.filter(t => t.status === "open").length,
  }), [current, tasks]);

  const prevCounts = useMemo(() => ({
    total:    previous.length,
    critical: previous.filter(p => p.severity === "critical" && !p.resolved).length,
    resolved: previous.filter(p =>  p.resolved).length,
  }), [previous]);

  /* bar chart: by category stacked by severity */
  const barData = useMemo(() => {
    const cats = categories.length ? categories : [...new Set(chartPosts.map(p => p.category).filter(Boolean))];
    const map  = {};
    cats.forEach(c => { map[c] = { category: c.length > 8 ? c.slice(0,7)+"…" : c, critical: 0, moderate: 0, low: 0 }; });
    chartPosts.forEach(p => {
      const k = p.category;
      if (!k) return;
      if (!map[k]) map[k] = { category: k.length > 8 ? k.slice(0,7)+"…" : k, critical: 0, moderate: 0, low: 0 };
      map[k][p.severity || "low"] = (map[k][p.severity || "low"] || 0) + 1;
    });
    return Object.values(map)
      .sort((a, b) => (b.critical + b.moderate + b.low) - (a.critical + a.moderate + a.low))
      .slice(0, 6);
  }, [chartPosts, categories]);

  /* pie chart: severity breakdown */
  const pieData = useMemo(() => [
    { name: "Critical", value: counts.critical, color: "var(--pp-crit)" },
    { name: "Moderate", value: counts.moderate, color: "var(--pp-mod)" },
    { name: "Low",      value: counts.low,      color: "var(--pp-low)" },
  ].filter(d => d.value > 0), [counts]);

  /* line chart: daily volume */
  const lineData = useMemo(() => {
    const dayMap = {};
    chartPosts.forEach(p => {
      const d = toDate(p.createdAt);
      if (!d) return;
      const k = fmtDay(d);
      dayMap[k] = (dayMap[k] || 0) + 1;
    });
    const days = Object.entries(dayMap).sort((a, b) =>
      new Date("2000/" + a[0]) - new Date("2000/" + b[0])
    );
    const W = 4;
    return days.map(([date, count], i) => {
      const win = days.slice(Math.max(0, i - W + 1), i + 1);
      const avg = Math.round((win.reduce((s, [, v]) => s + v, 0) / win.length) * 10) / 10;
      return { date, count, avg };
    });
  }, [chartPosts]);

  /* heatmap: hour × day-of-week */
  const heatData = useMemo(() => {
    const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
    chartPosts.forEach(p => {
      const d = toDate(p.createdAt);
      if (!d) return;
      grid[d.getDay()][d.getHours()]++;
    });
    return grid;
  }, [chartPosts]);

  /* top categories: current vs previous */
  const topCats = useMemo(() => {
    const catList = categories.length ? categories : [...new Set(all.map(p => p.category).filter(Boolean))];
    const count = (arr, cat) => arr.filter(p => p.category === cat).length;
    return catList
      .map(c => ({ name: c, cur: count(current, c), prev: count(previous, c) }))
      .sort((a, b) => b.cur - a.cur)
      .slice(0, 6);
  }, [all, current, previous, categories]);

  const topMax = Math.max(1, ...topCats.map(c => Math.max(c.cur, c.prev)));

  /* delta helpers */
  const totalDelta   = deltaBadge(counts.total,    prevCounts.total);
  const critDelta    = deltaBadge(counts.critical,  prevCounts.critical);
  const resolvedDelta = deltaBadge(counts.resolved, prevCounts.resolved);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

      {/* Filter bar */}
      <div className="pp-filter-bar">
        <Segmented value={range} onChange={setRange} options={[
          { value: "1d", label: "Today" },
          { value: "7d", label: "7d" },
          { value: "30d", label: "30d" },
          { value: "90d", label: "90d" },
          { value: "1y", label: "1y" },
          { value: "all", label: "All" },
        ]} />
        <div style={{ width: 1, height: 24, background: "var(--border)" }} />
        <select className="pp-select" style={{ height: 32, width: 160 }}
          value={filterCategory} onChange={e => setFilterCat(e.target.value)}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="pp-select" style={{ height: 32, width: 140 }}
          value={filterSeverity} onChange={e => setFilterSev(e.target.value)}>
          <option value="">All severity</option>
          <option value="critical">Critical</option>
          <option value="moderate">Moderate</option>
          <option value="low">Low</option>
        </select>
        <div style={{ flex: 1 }} />
        <Button variant="secondary" size="sm" icon={<PPIcons.IconExternal />}>Export</Button>
      </div>

      {/* Stat grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
        <StatTile label="Total reports"    value={counts.total}     delta={totalDelta.label}    deltaTone={totalDelta.tone} />
        <StatTile label="Critical · open"  value={counts.critical}  delta={critDelta.label}     deltaTone={critDelta.tone}  tone="crit" />
        <StatTile label="Moderate · open"  value={counts.moderate}  delta="—"                  deltaTone="flat"            tone="mod" />
        <StatTile label="Low · open"       value={counts.low}       delta="—"                  deltaTone="flat"            tone="low" />
        <StatTile label="Resolved"         value={counts.resolved}  delta={resolvedDelta.label} deltaTone={resolvedDelta.tone} tone="low" />
        <StatTile label="Open tasks"       value={counts.openTasks} delta="—"                  deltaTone="flat" />
      </div>

      {/* Charts row 1 */}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 12 }}>
        <ChartCard
          title="Reports by category"
          sub="Stacked by severity"
          chart={h => (
            <ResponsiveContainer width="100%" height={h}>
              <BarChart data={barData} barSize={22} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 3" stroke="var(--divider)" vertical={false} />
                <XAxis dataKey="category" tick={{ fontSize: 10, fill: "var(--text-faint)", fontFamily: "var(--font-mono)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "var(--text-faint)" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<PPTooltip />} cursor={{ fill: "var(--surface-2)" }} />
                <Bar dataKey="critical" stackId="a" fill="var(--pp-crit)" name="Critical" radius={[0, 0, 0, 0]} />
                <Bar dataKey="moderate" stackId="a" fill="var(--pp-mod)" name="Moderate" />
                <Bar dataKey="low"      stackId="a" fill="var(--pp-low)" name="Low"      radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        />
        <ChartCard
          title="Severity breakdown"
          sub={`${counts.total} reports in window`}
          chart={h => (
            <ResponsiveContainer width="100%" height={h}>
              <PieChart>
                <Pie data={pieData} cx="40%" cy="50%" innerRadius="42%" outerRadius="70%"
                  dataKey="value" paddingAngle={2} nameKey="name">
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip content={<PPTooltip />} />
                <Legend iconType="circle" iconSize={8}
                  formatter={v => <span style={{ fontSize: 11, color: "var(--text-dim)" }}>{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          )}
        />
      </div>

      {/* Charts row 2 */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <ChartCard
          title="Volume over time"
          sub="Daily count with 4-day rolling avg"
          chart={h => (
            <ResponsiveContainer width="100%" height={h}>
              <AreaChart data={lineData} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                <defs>
                  <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="var(--accent)" stopOpacity={0.18} />
                    <stop offset="95%" stopColor="var(--accent)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 3" stroke="var(--divider)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: "var(--text-faint)", fontFamily: "var(--font-mono)" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: "var(--text-faint)" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<PPTooltip />} />
                <Area type="monotone" dataKey="count" stroke="var(--accent)" fill="url(#areaGrad)" strokeWidth={2} dot={false} name="Reports" />
                <Area type="monotone" dataKey="avg" stroke="var(--text-dim)" fill="none" strokeWidth={1.5} strokeDasharray="3 3" dot={false} name="4-day avg" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        />
        <ChartCard
          title="Activity heat map"
          sub="Hour × day of week"
          chart={h => (
            <div style={{ width: "100%", height: h }}>
              <Heatmap data={heatData} />
            </div>
          )}
        />
      </div>

      {/* Top categories */}
      <div className="pp-surface">
        <SectionHead
          title="Top friction categories"
          sub="Current window vs previous"
          right={<span style={{ fontSize: 11, color: "var(--text-faint)", fontFamily: "var(--font-mono)" }}>current · prev</span>}
        />
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          {topCats.length === 0 ? (
            <div style={{ color: "var(--text-faint)", fontSize: 13, textAlign: "center", padding: 16 }}>No data in this window</div>
          ) : topCats.map(c => (
            <CategoryBar key={c.name} name={c.name} cur={c.cur} prev={c.prev} max={topMax} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
